import React from "react";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";

interface Props {
  show: boolean;
  onClose: () => void;
}

const sectionStyle: React.CSSProperties = {
  marginBottom: "22px",
};

const sectionTitleStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "var(--theme-text-strong)",
  marginBottom: "8px",
};

const iconBadgeStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  background: "var(--theme-highlight)",
  color: "var(--theme-accent)",
  fontSize: "0.95rem",
  flexShrink: 0,
};

const paragraphStyle: React.CSSProperties = {
  margin: "0 0 8px 0",
  lineHeight: 1.55,
  color: "var(--theme-text-soft)",
};

const noteStyle: React.CSSProperties = {
  fontSize: "0.88rem",
  color: "var(--theme-muted)",
  fontStyle: "italic",
  margin: 0,
};

const DemoWelcomeModal: React.FC<Props> = ({ show, onClose }) => {
  return (
    <CModal show={show} onClose={onClose} size="lg">
      <CModalHeader closeButton>
        <CModalTitle>Guía de tu cuenta demo</CModalTitle>
      </CModalHeader>
      <CModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Brand header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "14px",
            marginBottom: "22px",
            paddingBottom: "18px",
            borderBottom: "1px solid var(--theme-border)",
          }}
        >
          <img
            src="/smart-logo-no-text-white.webp"
            alt="SmartDoc"
            style={{
              height: "64px",
              width: "64px",
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(28%) sepia(79%) saturate(700%) hue-rotate(143deg) brightness(95%)",
            }}
          />
          <span
            style={{
              fontWeight: "bold",
              fontSize: "2.4rem",
              color: "var(--theme-accent)",
            }}
          >
            SmartDoc
          </span>
        </div>

        {/* Intro */}
        <p
          style={{
            fontSize: "1rem",
            lineHeight: 1.6,
            color: "var(--theme-text-soft)",
            marginBottom: "24px",
          }}
        >
          Esta es una versión de prueba pensada para que conozcas SmartDoc a tu
          ritmo. A continuación te contamos todo lo que podrás hacer aquí y cómo
          aprovecharla al máximo.
        </p>

        {/* Sobre la demo */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-flask" />
            </span>
            Sobre esta demo
          </div>
          <p style={paragraphStyle}>
            En esta demo podrás probar la emisión de comprobantes electrónicos
            de punta a punta. Para que puedas hacerlo sin configuraciones
            previas, ya dejamos todo listo: un <strong>establecimiento</strong>,{" "}
            <strong>punto de expedición</strong>, <strong>timbrado</strong> y{" "}
            <strong>firma digital</strong> de prueba. Tu única tarea será probar
            la emisión.
          </p>
          <p style={noteStyle}>
            En la versión real podrás cargar tus propios establecimientos,
            puntos de expedición, timbrado, firma digital, actividades
            económicas y demás datos de tu empresa.
          </p>
        </div>

        {/* Cómo emitir */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-file-invoice" />
            </span>
            Cómo navegar y emitir documentos
          </div>
          <p style={paragraphStyle}>
            En el menú lateral, ingresa al apartado del documento que quieras
            emitir (factura, nota de crédito, etc.). Verás una lista con todos
            los documentos del tipo seleccionado y filtros para encontrarlos
            fácilmente. Para emitir uno nuevo, haz clic en el botón{" "}
            <strong>"Nuevo"</strong>: se abrirá un formulario que, al guardarlo,
            automáticamente:
          </p>
          <ul
            style={{
              marginTop: "4px",
              marginBottom: "8px",
              color: "var(--theme-text-soft)",
              lineHeight: 1.6,
            }}
          >
            <li>Genera el XML firmado con validez legal.</li>
            <li>Lo envía a la DNIT para su aprobación.</li>
            <li>Genera el KuDE (factura gráfica en PDF).</li>
            <li>Envía el KuDE al cliente por correo electrónico.</li>
          </ul>
          <p style={paragraphStyle}>
            Desde cada fila de la lista también podrás descargar los documentos
            o ejecutar acciones adicionales.
          </p>
          <p style={noteStyle}>
            <i className="fa fa-exclamation-triangle mr-1" />
            Los documentos generados en la demo no tienen ningún valor fiscal.
            Todo lo emitido aquí es únicamente a efectos de prueba.
          </p>
        </div>

        {/* Estados */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-tasks" />
            </span>
            Estados de los documentos
          </div>
          <p style={paragraphStyle}>
            Los documentos se envían automáticamente al guardarlos, pero la DNIT
            no siempre los aprueba de forma instantánea. Por eso, en la lista
            verás una columna con el estado actual de cada uno:{" "}
            <em>generado</em>, <em>subido</em>, <em>aprobado</em>,{" "}
            <em>anulado</em>, <em>error</em>, entre otros.
          </p>
          <p style={paragraphStyle}>
            Si hubo un error en la generación o la DNIT rechazó el documento, se
            indicará en la lista y podrás editarlo para corregirlo. SmartDoc
            reenvía periódicamente los documentos hasta que son aprobados. Una
            vez aprobado, el documento queda inmutable y ya no se puede
            modificar.
          </p>
        </div>

        {/* Descargas */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-download" />
            </span>
            Descargas
          </div>
          <p style={paragraphStyle}>
            En cada lista podrás descargar individualmente el PDF del KuDE o el
            XML correspondiente a cada documento. Además, en la parte superior
            de cada lista encontrarás la opción de descargar en lote: un{" "}
            <strong>PDF</strong> o un <strong>Excel</strong> con todos los
            documentos que coincidan con el filtro aplicado.
          </p>
        </div>

        {/* Conectividad */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-plug" />
            </span>
            Conectividad con otros sistemas
          </div>
          <p style={paragraphStyle}>
            Lo que estás viendo es el <strong>panel</strong> de SmartDoc, pero
            SmartDoc también se puede interconectar con otros sistemas. Para eso
            contamos con el módulo de <strong>Conectividad</strong>, desde donde
            podrás configurar el acceso a nuestra API y administrar webhooks.
            Con esto podrás enviar y recibir información desde otros sistemas o
            aplicaciones sin necesidad de llenar los formularios manualmente en
            el panel.
          </p>
        </div>

        {/* Extras */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>
            <span style={iconBadgeStyle}>
              <i className="fa fa-star" />
            </span>
            Extras
          </div>

          <div style={{ marginTop: "10px", marginBottom: "12px" }}>
            <div
              style={{ fontWeight: 600, color: "var(--theme-text-strong)", marginBottom: "4px" }}
            >
              Conectar tu Inteligencia Artificial
            </div>
            <p style={paragraphStyle}>
              Podrás conectar el asistente que ya usás —Claude, ChatGPT, Gemini
              y otros— a SmartDoc y preguntarle sobre tu facturación:{" "}
              <em>cuánto facturé este mes comparado con el anterior</em>,{" "}
              <em>qué producto me facturó más</em>,{" "}
              <em>quién es mi mejor cliente</em>, y todo lo que se te ocurra.
              Cada usuario tiene los mismos permisos que en este panel. Para
              empezar, activala desde <strong>Inteligencia Artificial</strong>{" "}
              en el menú.
            </p>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div
              style={{ fontWeight: 600, color: "var(--theme-text-strong)", marginBottom: "4px" }}
            >
              Documentos recibidos
            </div>
            <p style={paragraphStyle}>
              Además de emitir, podrás registrar los comprobantes tributarios
              que hayas recibido de terceros, ya sea cargando el CDC o
              ingresando los datos manualmente, para tener toda tu información
              tributaria en un solo lugar.
            </p>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div
              style={{ fontWeight: 600, color: "var(--theme-text-strong)", marginBottom: "4px" }}
            >
              Configuración de correo
            </div>
            <p style={paragraphStyle}>
              Por defecto, los correos a los clientes se envían desde{" "}
              <code>@facturas.smartdoc.com.py</code>. Si cuentas con un servicio
              de correo propio, puedes configurarlo para que los envíos se hagan
              desde tu propio dominio.{" "}
              <span style={{ color: "var(--theme-muted)" }}>
                (No se puede usar un correo personal para esto.)
              </span>
            </p>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div
              style={{ fontWeight: 600, color: "var(--theme-text-strong)", marginBottom: "4px" }}
            >
              KuDE personalizado
            </div>
            <p style={paragraphStyle}>
              Puedes solicitar cambios en el formato del KuDE que se envía al
              cliente, para que refleje la imagen o las necesidades de tu
              empresa.
            </p>
          </div>

          <div>
            <div
              style={{ fontWeight: 600, color: "var(--theme-text-strong)", marginBottom: "4px" }}
            >
              Portal de Consulta
            </div>
            <p style={paragraphStyle}>
              Tendrás un enlace exclusivo que podés compartir con tus clientes
              o incrustar en tu sitio web. Desde ahí, ellos podrán consultar y
              descargar los documentos electrónicos que les emitiste
              (facturas, notas de crédito, débito y remisión) ingresando su
              RUC y el número de documento, sin necesidad de iniciar sesión.
            </p>
          </div>
        </div>

        {/* Closing */}
        <div
          style={{
            textAlign: "center",
            marginTop: "28px",
            padding: "18px",
            background: "var(--theme-surface-raised)",
            borderRadius: "10px",
            border: "1px solid var(--theme-border)",
          }}
        >
          <div
            style={{
              fontSize: "1.05rem",
              fontWeight: 700,
              color: "var(--theme-text-strong)",
              marginBottom: "6px",
            }}
          >
            ¡Gracias por probar SmartDoc!
          </div>
          <div style={{ fontSize: "0.92rem", color: "var(--theme-muted)" }}>
            Puedes volver a abrir esta guía en cualquier momento desde el banner
            superior.
          </div>
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="primary" onClick={onClose}>
          Empezar a probar
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default DemoWelcomeModal;
